import { ServerChat } from '../types/chats';

export interface Message {
  role: string;
  content: string;
  moduleDisplayName?: string;
}

export interface Chat {
  id: string;
  title: string;
  messages: Message[];
  createdAt: number;
  updatedAt: number;
  selectedModule?: string;
  raiseTicket?: boolean;
}

const STORAGE_KEY = 'mygate_chat_history';

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts or older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> (c === 'x' ? 0 : 1);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// A brand-new chat while signed in: not persisted anywhere yet (no dedicated
// "create chat" endpoint — the server auto-vivifies the chat document on the
// first /api/chat call for this id, exactly like the anonymous flow below).
// Kept out of localStorage entirely so it never leaks into the anonymous
// history shown after sign-out.
export function createEphemeralChat(): Chat {
  const now = Date.now();
  return { id: generateUUID(), title: 'New Chat', messages: [], createdAt: now, updatedAt: now };
}

// Maps a server-fetched chat (signed-in history) into the same shape the UI
// already renders for anonymous/local chats, so ChatWindow/Sidebar need no
// awareness of which source a chat came from.
export function fromServerChat(chat: ServerChat): Chat {
  return {
    id: chat.chat_id,
    title: chat.title,
    messages: chat.messages.map((m) => ({
      role: m.role,
      content: m.content,
      moduleDisplayName: m.module_display_name ?? undefined,
    })),
    createdAt: new Date(chat.created_at).getTime(),
    updatedAt: new Date(chat.updated_at).getTime(),
    selectedModule: chat.module_type ?? undefined,
    raiseTicket: chat.ticket_raisable,
  };
}

export const chatStorage = {
  // Get all chats
  getAllChats(): Chat[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return [];
      const parsed = JSON.parse(data);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item: unknown): item is Chat => {
        if (!item || typeof item !== 'object') return false;
        const c = item as Record<string, unknown>;
        return (
          typeof c.id === 'string' &&
          typeof c.title === 'string' &&
          Array.isArray(c.messages) &&
          typeof c.createdAt === 'number' &&
          typeof c.updatedAt === 'number'
        );
      });
    } catch (error) {
      console.error('Error reading chats from localStorage:', error);
      return [];
    }
  },

  // Get a specific chat by ID
  getChat(chatId: string): Chat | null {
    const chats = this.getAllChats();
    return chats.find(chat => chat.id === chatId) || null;
  },

  // Create a new chat
  createChat(): Chat {
    const newChat: Chat = {
      id: generateUUID(),
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    const chats = this.getAllChats();
    chats.unshift(newChat); // Add to beginning
    this.saveChats(chats);

    return newChat;
  },

  // Update a chat
  updateChat(chatId: string, updates: Partial<Chat>): void {
    const chats = this.getAllChats();
    const index = chats.findIndex(chat => chat.id === chatId);

    if (index !== -1) {
      let safeUpdates = { ...updates };
      if (safeUpdates.messages && safeUpdates.messages.length > 200) {
        safeUpdates = { ...safeUpdates, messages: safeUpdates.messages.slice(-200) };
      }
      chats[index] = {
        ...chats[index],
        ...safeUpdates,
        updatedAt: Date.now(),
      };
      this.saveChats(chats);
    }
  },

  // Delete a chat
  deleteChat(chatId: string): void {
    const chats = this.getAllChats();
    const filtered = chats.filter(chat => chat.id !== chatId);
    this.saveChats(filtered);
  },

  // Save chats to localStorage
  saveChats(chats: Chat[]): void {
    try {
      const serialized = JSON.stringify(chats);
      if (serialized.length > 4 * 1024 * 1024) {
        // Storage approaching limit — trim oldest chats to stay under 4MB
        const trimmed = chats.slice(0, Math.floor(chats.length / 2));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
        return;
      }
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Error saving chats to localStorage:', error);
    }
  },

  // Clear all chats
  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
  },
};
